"use client";

import { useState } from "react";
import { useAnimatedNumber } from "@/hooks/useAnimatedNumber";
import SegmentedToggle from "./SegmentedToggle";

// ─── Statische Demo-Daten für die Landingpage ──────────────────────────────
// Kein Auth-/API-Bezug bewusst: diese Preview soll unabhängig vom
// Anmeldestatus immer gleich aussehen. Angelehnt an die Platzhalterdaten aus
// components/tool/SzenarioVergleichView.tsx. Der Status-Quo/Szenario-
// Umschalter ist eine echte, im Tool selbst vorhandene Unterscheidung.

type Ansicht = "status" | "szenario";

const MODAL_SPLIT: Record<Ansicht, { label: string; value: number; color: string }[]> = {
  status: [
    { label: "Auto", value: 49.5, color: "#1e3a5f" },
    { label: "ÖPNV", value: 21.7, color: "#2563eb" },
    { label: "Zu Fuß", value: 15.2, color: "#16a34a" },
    { label: "Fahrrad", value: 10.6, color: "#f59e0b" },
    { label: "Carpool", value: 3.0, color: "#94a3b8" },
  ],
  szenario: [
    { label: "Auto", value: 35, color: "#1e3a5f" },
    { label: "ÖPNV", value: 30, color: "#2563eb" },
    { label: "Zu Fuß", value: 13, color: "#16a34a" },
    { label: "Fahrrad", value: 18, color: "#f59e0b" },
    { label: "Carpool", value: 4, color: "#94a3b8" },
  ],
};

const SCORES = ["A", "B", "C", "D", "E"];
const SCORE_COLORS: Record<string, string> = {
  A: "bg-green-500", B: "bg-lime-400", C: "bg-yellow-400", D: "bg-orange-400", E: "bg-red-500",
};
const MOBISCORE: Record<Ansicht, string> = { status: "C", szenario: "B" };

const KENNZAHLEN = [
  { label: "CO₂-Emissionen", status: 29000, szenario: 23400, einheit: "t" },
  { label: "Mobilitätskosten", status: 300000, szenario: 225000, einheit: "€" },
];

function formatNumber(n: number): string {
  const rounded = Math.round(n);
  if (Math.abs(rounded) >= 1_000_000) return (rounded / 1_000_000).toFixed(1).replace(".", ",") + " Mio";
  return rounded.toLocaleString("de-DE");
}

function DeltaBadge({ prozent }: { prozent: number }) {
  const istBesser = prozent < 0;
  return (
    <span className={`text-xs font-semibold ${istBesser ? "text-green-600" : "text-red-500"}`}>
      {prozent > 0 ? "+" : ""}
      {prozent.toFixed(1)}%
    </span>
  );
}

function KennzahlKarte({
  kennzahl,
  ansicht,
}: {
  kennzahl: (typeof KENNZAHLEN)[number];
  ansicht: Ansicht;
}) {
  const wert = useAnimatedNumber(ansicht === "szenario" ? kennzahl.szenario : kennzahl.status);
  const prozent = ((kennzahl.szenario - kennzahl.status) / kennzahl.status) * 100;

  return (
    <div className="bg-blue-50/60 rounded-lg p-2.5 sm:p-3 min-w-0">
      <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mb-1 truncate">{kennzahl.label}</p>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 mb-2">
        <span className="text-sm sm:text-lg font-bold text-blue-700 tabular-nums whitespace-nowrap">
          {formatNumber(wert)} {kennzahl.einheit}
        </span>
        <DeltaBadge prozent={prozent} />
      </div>
      <div className="flex flex-col gap-1">
        <MiniModalBar daten={MODAL_SPLIT.status} aktiv={ansicht === "status"} />
        <MiniModalBar daten={MODAL_SPLIT.szenario} aktiv={ansicht === "szenario"} />
      </div>
    </div>
  );
}

function MiniModalBar({ daten, aktiv }: { daten: { value: number; color: string }[]; aktiv: boolean }) {
  return (
    <div
      className={`w-full h-2 rounded-full overflow-hidden flex bg-gray-100 transition-opacity ${aktiv ? "opacity-100" : "opacity-35"}`}
    >
      {daten.map((d, i) => (
        <div key={i} style={{ width: `${d.value}%`, backgroundColor: d.color }} />
      ))}
    </div>
  );
}

export default function SzenarioVergleichPreview() {
  const [ansicht, setAnsicht] = useState<Ansicht>("szenario");

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex justify-end">
        <SegmentedToggle
          value={ansicht}
          onChange={setAnsicht}
          options={[
            { value: "status", label: "Status Quo" },
            { value: "szenario", label: "Szenario" },
          ]}
        />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-blue-50/60 rounded-lg p-2.5 sm:p-3 min-w-0">
          <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mb-1.5 sm:mb-2 truncate">MobiScore</p>
          <div className="flex gap-1">
            {SCORES.map((s) => (
              <div
                key={s}
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded flex items-center justify-center text-[10px] sm:text-xs font-bold text-white transition-transform ${SCORE_COLORS[s]}
                  ${s === MOBISCORE[ansicht] ? "ring-2 ring-offset-1 ring-gray-800 scale-110" : "opacity-40"}`}
              >
                {s}
              </div>
            ))}
          </div>
        </div>

        {KENNZAHLEN.map((k) => (
          <KennzahlKarte key={k.label} kennzahl={k} ansicht={ansicht} />
        ))}
      </div>
    </div>
  );
}
